import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { HiCheck, HiLockClosed } from "react-icons/hi";
import { toast } from "react-toastify";
import ConfirmModal from "../../../public/ConfirmModal";
import { renderHiddenToken } from "../../../../utils/utils";
import { useUpdateToken } from "../../../../hooks/users";
import useAuth from "../../../../hooks/useAuth";
import { queryClient } from "../../../../providers/QueryProvider";
import SecureStorage from "../../../../utils/secureStorage";
import { PlanTier, usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import { UpgradePromptContent } from "../../../atoms/UpgradePromptContent";
import { PlansModal } from "../../../modals/PlansModal";
import { Copy } from "../../../atoms/Copy";
import { CustomButton } from "../../../atoms/CustomButton";
import { useCopyToClipboard } from "../../../atoms/useCopyToClipboard";

const MCPPromoSection = ({ t }: { t: (key: string) => string }) => {
  const setupCommand = t(
    "dashboard.develop.swaggerInfos.mcp_promo.setup_command"
  );

  return (
    <div className="w-full">
      <div className="mb-4">
        <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
          {t("dashboard.develop.swaggerInfos.mcp_promo.badge")}
        </span>
        <p className="mt-1.5 text-sm text-gray-500">
          {t("dashboard.develop.swaggerInfos.mcp_promo.subtitle")}
        </p>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="flex-shrink-0 lg:w-1/3">
          <img
            src="https://cdn.1cdn.app/application/UMD/26033023472260_claude_sum.png"
            alt="Claude Code AI assistant"
            className="h-auto w-full rounded-lg border border-gray-200"
          />
        </div>

        <div className="flex-1">
          <div className="mb-4 space-y-2">
            {["benefit_1", "benefit_2", "benefit_3"].map((key) => (
              <div key={key} className="flex items-center gap-2">
                <HiCheck className="h-4 w-4 flex-shrink-0 text-gray-400" />
                <span className="text-sm text-gray-700">
                  {t(`dashboard.develop.swaggerInfos.mcp_promo.${key}`)}
                </span>
              </div>
            ))}
          </div>

          <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-500">
              {t("dashboard.develop.swaggerInfos.mcp_promo.traditional")}
            </div>
            <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700">
              {t("dashboard.develop.swaggerInfos.mcp_promo.with_mcp")}
            </div>
          </div>

          <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
            <div className="mb-2 text-xs text-gray-500">
              {t("dashboard.develop.swaggerInfos.mcp_promo.setup_label")}
            </div>
            <div className="font-mono text-xs text-gray-700 sm:text-sm">
              <Copy target={setupCommand} />
            </div>
          </div>

          <div className="mt-6 rounded-lg border border-gray-200 p-4">
            <h4 className="text-sm font-semibold text-gray-900">
              {t("dashboard.develop.swaggerInfos.mcp_promo.included_title")}
            </h4>
            <p className="mb-3 mt-0.5 text-sm text-gray-500">
              {t("dashboard.develop.swaggerInfos.mcp_promo.included_subtitle")}
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {[
                "included_1",
                "included_2",
                "included_3",
                "included_4",
                "included_5",
                "included_6",
                "included_7",
              ].map((key) => (
                <div key={key} className="flex items-center gap-2">
                  <HiCheck className="h-4 w-4 flex-shrink-0 text-gray-400" />
                  <span className="text-sm text-gray-700">
                    {t(`dashboard.develop.swaggerInfos.mcp_promo.${key}`)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-gray-200 p-4">
        <h4 className="text-sm font-semibold text-gray-900">
          {t("dashboard.develop.swaggerInfos.mcp_promo.free_access_title")}
        </h4>
        <p className="mt-0.5 text-sm leading-relaxed text-gray-500">
          {t(
            "dashboard.develop.swaggerInfos.mcp_promo.free_access_description"
          )}
        </p>
        <a
          href="/control-panel/support"
          className="mt-2 inline-block text-sm font-medium text-blue-600 hover:text-blue-700"
        >
          {t("dashboard.develop.swaggerInfos.mcp_promo.free_access_cta")}
        </a>
      </div>
    </div>
  );
};

const SwaggerInfos = ({
  query,
  disabled,
}: {
  query: any;
  disabled: boolean;
}) => {
  const { customer_identification, user, setUser } = useAuth();
  const { mutate: updateToken, isLoading: isUpdatingToken } = useUpdateToken();
  const { copy } = useCopyToClipboard();

  const permissions = usePermissions(user);
  const canWriteApis = permissions.can(Cap.APIS, Level.WRITE);

  const [t] = useTranslation();
  const [swaggerInfos, setSwaggerInfos] = useState<{
    username: string;
    password: string;
    entity_identification: string;
  }>();
  const [openModal, setOpenModal] = useState(false);
  const [showPlansModal, setShowPlansModal] = useState(false);

  useEffect(() => {
    const userIdentification = SecureStorage.getUserIdentification();
    const entityIdentification = SecureStorage.getEntityIdentification();

    if (userIdentification && entityIdentification) {
      setSwaggerInfos({
        username: userIdentification,
        entity_identification: entityIdentification,
        password: query?.access_token,
      });
    }
  }, [query?.access_token]);

  const label = (key: string) =>
    (t(key) as string).replace(/\s*:\s*$/, "").trim();

  const credentialRows = [
    {
      item: "entity_identification",
      label: label("dashboard.develop.swaggerInfos.item-03"),
      value: swaggerInfos?.entity_identification ?? "",
      copyValue: swaggerInfos?.entity_identification ?? "",
      action: null,
    },
    {
      item: "username",
      label: label("dashboard.develop.swaggerInfos.item-01"),
      value: swaggerInfos?.username ?? "",
      copyValue: swaggerInfos?.username ?? "",
      action: null,
    },
    {
      item: "password",
      label: label("dashboard.develop.swaggerInfos.item-02"),
      value: swaggerInfos
        ? renderHiddenToken(swaggerInfos.password as string)
        : "",
      copyValue: swaggerInfos?.password ?? "",
      action:
        !disabled && canWriteApis ? (
          <CustomButton
            variant="secondary"
            size="xs"
            className="shrink-0"
            isLoading={isUpdatingToken}
            onClick={() => setOpenModal(true)}
            title={t("dashboard.develop.swaggerInfos.btn-01")}
          />
        ) : null,
    },
  ];

  const UpdateTokenHandler = () => {
    onUpdatetoken();
    setOpenModal(false);
  };

  const onUpdatetoken = () => {
    updateToken(
      {
        customer_identification: customer_identification,
        entityIdentification: user.entity_identification,
      },
      {
        onSuccess: (res) => {
          queryClient.clear();
          if (typeof window !== "undefined") {
            SecureStorage.updateAuthData({ access_token: res.access_token });

            setUser({ ...user, access_token: res.access_token });

            void copy(res.access_token);

            toast.info(t("dashboard.develop.swaggerInfos.tokenGenerated"));
          }
        },
      }
    );
  };

  const hasApiAccess = permissions.isAtLeastTier(PlanTier.LARGE_BUSINESS);

  if (!hasApiAccess) {
    return (
      <div className="flex flex-col items-start gap-6">
        <MCPPromoSection t={t} />

        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {t("dashboard.develop.swaggerInfos.header")}
          </h3>
          <p className="mt-0.5 text-sm text-gray-500">
            {t("dashboard.develop.swaggerInfos.upgrade_required")}
          </p>
        </div>

        <UpgradePromptContent
          icon={HiLockClosed}
          iconTheme="blue"
          title={t("dashboard.develop.swaggerInfos.api_locked_title")}
          description={t("dashboard.develop.swaggerInfos.api_locked_desc")}
          benefits={[
            {
              id: "api-docs",
              text: t("dashboard.develop.swaggerInfos.benefit_docs"),
            },
            {
              id: "api-tokens",
              text: t("dashboard.develop.swaggerInfos.benefit_tokens"),
            },
            {
              id: "api-integration",
              text: t("dashboard.develop.swaggerInfos.benefit_integration"),
            },
          ]}
          upgradeText={t("dashboard.develop.swaggerInfos.upgrade_plan")}
          onUpgrade={() => {
            setShowPlansModal(true);
          }}
          className="w-full"
        />

        <PlansModal
          showPlansModal={showPlansModal}
          setShowPlansModal={setShowPlansModal}
          targetPlan={PlanTier.LARGE_BUSINESS}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-start gap-6">
      <MCPPromoSection t={t} />

      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          {t("dashboard.develop.swaggerInfos.header")}
        </h3>
        <p className="mt-0.5 text-sm text-gray-500">
          {t("dashboard.develop.swaggerInfos.text-01")}
        </p>
      </div>

      <dl className="grid w-full grid-cols-1 items-center gap-x-3 gap-y-1.5 text-xs sm:grid-cols-[11rem_1fr] sm:gap-y-2.5">
        {credentialRows.map((row) => (
          <Fragment key={row.item}>
            <dt className="text-gray-500">{row.label}</dt>
            <dd className="flex min-w-0 items-center gap-2">
              <span className="truncate font-mono text-gray-700">
                {row.value}
              </span>
              <Copy target={row.copyValue} iconOnly title={row.label} />
              {row.action}
            </dd>
          </Fragment>
        ))}
      </dl>

      <div className="w-full md:ml-auto md:w-fit">
        <CustomButton
          variant="primary"
          href="https://api.super-user-manager.com/sum-connector/api/reference"
          {...({ target: "_blank", rel: "noreferrer" } as Record<
            string,
            string
          >)}
          className={disabled ? "pointer-events-none opacity-50" : undefined}
          aria-disabled={disabled}
          title={t("dashboard.develop.swaggerInfos.item-04")}
        />
      </div>

      <ConfirmModal
        show={openModal && !isUpdatingToken}
        onClose={setOpenModal}
        title={t("dashboard.develop.swaggerInfos.text-02")}
        description={t("dashboard.develop.swaggerInfos.text-03")}
        cancelBtnName={t("common.cancel")}
        cancelBtnFn={setOpenModal}
        confirmBtnName={t("dashboard.develop.swaggerInfos.btn-03")}
        confirmBtnFn={UpdateTokenHandler}
        disabled={disabled}
      />
    </div>
  );
};

export default SwaggerInfos;
